import { useState, useContext, useEffect, useRef } from 'react';

import cn from 'classnames/bind';
import { useScrollProgress, useToggleState } from 'hooks';
import Head from 'next/head';
import {
  Button,
  Col,
  Container,
  Row,
  SEO,
  TypographyPage,
} from 'ui';
import BlogCard from 'ui/components/blog/BlogCard';
import BlogCTA from 'ui/components/blog/BlogCTA';
import BlogCTAMobile from 'ui/components/blog/BlogCTAMobile';
import BlogMetaInfo from 'ui/components/blog/BlogMetaInfo';
import CTASlide from 'ui/components/CTASlide';
import { Context as LayoutContext } from 'ui/containers/LayoutContext';
import BlogContent from 'ui/layouts/blog/BlogContent';
import ExploreBlogTypesMobileSection, {
  defaultColumnProps,
} from 'ui/layouts/ExploreBlogTypesMobileSection';
import ExploreBlogTypesSection from 'ui/layouts/ExploreBlogTypesSection';
import PageContentMenu from 'ui/layouts/PageContentMenuTree';
import SectionObserver from 'ui/layouts/SectionObserver';
import SharingBlock from 'ui/layouts/SharingBlock';
import SingleResourcesHeader from 'ui/layouts/SingleResourcesHeader';
import StickyColumn from 'ui/layouts/StickyColumn';
import StickyHeader from 'ui/layouts/StickyHeader';
import { getSEOKeywords, getNewLabel } from 'utils';
import { flattenTree, generateTreeForPost } from 'utils/blog';
import { dateformat } from 'utils/datetime';
import getJsonLdPosting from 'utils/jsonLd/blogPosting';
import { generateBlogPostUrl } from 'utils/jsonLd/utils';
import getJsonLdWebPage from 'utils/jsonLd/webPage';
import { getArticleHref } from 'utils/links';

import s from './BlogPost.module.css';

const cx = cn.bind(s);

const type = process.env.NEXT_PUBLIC_LANDING_TYPE || '';

function BlogPost({ post, jsonLd, menu, layout: Layout }) {
  const { scrollProgressPercentage } = useScrollProgress();
  const wasCTAPopupOpened = useRef(false);
  const { isActive: isMobileCTAVisible, open: openMobileCTA } =
    useToggleState(false);
  useEffect(() => {
    if (
      !wasCTAPopupOpened.current &&
      scrollProgressPercentage > 35 &&
      window.innerWidth < 1024
    ) {
      wasCTAPopupOpened.current = true;
      openMobileCTA();
    }
  }, [scrollProgressPercentage, openMobileCTA]);
  const { breakpoint } = useContext(LayoutContext);
  const isMobile =
    breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md';
  const [active, setActive] = useState(menu?.key);
  const menuItems = menu?.children || [];
  const exploreThisTypeItems = post.exploreThisTypeItems || [];

  const hasExploreThisType = !!exploreThisTypeItems.length;
  const hasNavigationSidebar = !!(menu || hasExploreThisType);
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          key="product-jsonld"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd)
          }}
        />
      </Head>
      <SEO
        brandTitle={false}
        title={post.seo?.seo_title || post.metaTitle || post.title}
        description={
          post.seo?.meta_description || post.metaDescription || post.description
        }
        image={post.image}
        keywords={getSEOKeywords(post.seo)}
      />
      <SectionObserver
        key={post.title}
        bottom
        hasBlogNavigation
        items={menuItems}
        onActiveChange={setActive}
      >
        <Layout
          headerProps={{
            append: isMobile ? (
              <StickyHeader
                active={active}
                color="mint"
                isDense
                items={flattenTree(menuItems)}
              />
            ) : null,
            filled: true,
          }}
        >
          <SingleResourcesHeader
            bgImage={post.image}
            title={post.title}
            author={post.author}
          />
          <Container className={cx('pageContent')}>
            <Row>
              <Col
                {...defaultColumnProps}
                lg={{ span: 3, offset: 0 }}
                className={cx('info')}
              >
                <div className="text-sm text-gray-400 pb-1">
                  {dateformat.short(post.createdAt)}
                </div>
                <BlogMetaInfo
                  label={getNewLabel(post.createdAt)}
                  labelColor="mint"
                  readTime={post.readingTime || 30}
                  hasReadLabel
                />  
                {hasNavigationSidebar && (
                  <StickyColumn className={cx('menuColumn')}>
                    <BlogCTA className="mt-[5px] mb-10" />
                    {!!menu && (
                      <PageContentMenu
                        items={menuItems}
                        active={active}
                        variant="blog"
                      />
                    )}
                    {hasExploreThisType && (
                      <ExploreBlogTypesSection
                        items={exploreThisTypeItems}
                        isLanding={!!type}
                      />
                    )}
                  </StickyColumn>
                )}
              </Col>
              <TypographyPage
                {...defaultColumnProps}
                color="mint"
                as={Col}
                blog
              >
                <BlogContent body={post.body} />
              </TypographyPage>
            </Row>
          </Container>
          <CTASlide
            className="w-full max-w-[375px] px-[15px]"
            isActive={isMobileCTAVisible}
          >
            <BlogCTAMobile />
          </CTASlide>
          {hasExploreThisType && (
            <ExploreBlogTypesMobileSection
              items={exploreThisTypeItems}
              isLanding={!!type}
            />
          )}
          <Container>
            <Row>
              <Col md={{ offset: 1, span: 11 }}>
                <div className={cx('actions')}>
                  <SharingBlock
                    title={post.title}
                    url={
                      process.env.NEXT_PUBLIC_HOST +
                      getArticleHref(post.slug, { isLanding: !!type })
                    }
                  />
                  <div className={cx('backAction')}>
                    <Button
                      href="/blog"
                      as="a"
                      variant="text"
                      color="black"
                      hasIcon={true}
                    >
                      Back to articles
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
          {!!post.related?.length && (
            <section className={cx('alsoLike')}>
              <h2 className={cx('tp-header-2', 'alsoLikeTitle')}>
                You may also like
              </h2>
              <Container>
                <Row>
                  {post.related?.map((relatedPost, index) => (
                    <Col
                      key={index}
                      lg={{
                        offset: 2,
                        span: 8,
                      }}
                      xl={{
                        offset: 0,
                        span: 4,
                      }}
                    >
                      <div className={cx('alsoLikeCard')}>
                        <BlogCard
                          date={dateformat.short(relatedPost.createdAt)}
                          label={getNewLabel(relatedPost.createdAt)}
                          href={getArticleHref(relatedPost.slug, {
                            isLanding: !!type,
                          })}
                          title={relatedPost.title}
                          text={relatedPost.description}
                          author={relatedPost.author}
                          poster={relatedPost.image}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              </Container>
            </section>
          )}
        </Layout>
      </SectionObserver>
    </>
  );
}

BlogPost.prettyName = 'Blog Post';
BlogPost.getLayout = (children) => children;

async function getBlogModel(isLanding) {
  if (isLanding) {
    const { default: model } = await import('api/models/LandingBlogPost');
    return model;
  }
  const { default: model } = await import('api/models/BlogPost');
  return model;
}
export async function getStaticPaths() {
  const BlogModel = await getBlogModel(!!type);
  const allPosts = await BlogModel.slugs(type);
  return {
    paths: allPosts.map((post) => ({
      params: {
        slug: post.slug,
      },
    })),
    fallback: 'blocking',
  };
}
export async function getStaticProps({ params }) {
  const BlogModel = await getBlogModel(!!type);
  const post = await (() => {
    if (params.id) {
      return BlogModel.withConfig({
        useCdn: false,
        withCredentials: true,
      }).getById(params.id, type);
    }
    return BlogModel.getBySlug(params.slug, type);
  })();
  if (!post) {
    return {
      notFound: true,
      revalidate: 1,
    };
  }
  const options = { isCurrent: true, isInitial: true, levels: [2, 3] };
  const menu = generateTreeForPost(post, options) || null;
  const jsonLd  = getJsonLdWebPage({
    name: post.seo?.seo_title || post.metaTitle || post.title, 
    description:post.seo?.meta_description || post.metaDescription || post.description,  
    url: generateBlogPostUrl(type, post.slug),
    mainEntity: getJsonLdPosting({
      authorName: post?.author?.name,
      url: generateBlogPostUrl(type, post.slug),
      headline: post.seo?.seo_title || post.metaTitle || post.title,
      description: post.seo?.meta_description || post.metaDescription || post.description,
      image: post.image,
      dateCreated: post.createdAt,
      dateModified: post.updatedAt
    })
  })

  return {
    props: {
      post,
      jsonLd,
      menu,
    },
    revalidate: 30 * 60,
  };
}

export default BlogPost;
